import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { auth } from '../../firebase';
import onboardingService from '../../services/onboardingService';
import { useAuth } from '../../contexts/AuthContext';
import OnboardingProgress from './OnboardingProgress';
import CompanySetupStep from './steps/CompanySetupStep';
import AdminUserStep from './steps/AdminUserStep';
import InitialConfigStep from './steps/InitialConfigStep';
import WelcomeStep from './steps/WelcomeStep';

const STEP_DATA_KEYS = {
  1: 'adminUser',
  2: 'company',
  3: 'configuration',
  4: 'welcome'
};

const stableStringify = (value) => {
  if (value === undefined) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }

  return JSON.stringify(value);
};

const deepEqual = (a, b) => stableStringify(a) === stableStringify(b);

const stripAdminMeta = (raw = {}) => {
  const {
    isPartial,
    lastSaved,
    lastSavedAt,
    readyForSubmission,
    onboardingStatus,
    metadata,
    createdAt,
    updatedAt,
    ...rest
  } = raw;
  return rest;
};

const stripCompanyMeta = (raw = {}) => {
  const {
    isPartial,
    lastSaved,
    lastSavedAt,
    readyForSubmission,
    completedAt,
    metadata,
    createdAt,
    updatedAt,
    ...rest
  } = raw;
  return rest;
};

const stripConfigurationMeta = (raw = {}) => {
  const {
    isPartial,
    lastSaved,
    lastSavedAt,
    readyForSubmission,
    createdAt,
    updatedAt,
    ...rest
  } = raw;
  return rest;
};

const stripWelcomeMeta = (raw = {}) => {
  const {
    metadata,
    ...rest
  } = raw || {};
  return rest;
};

const normalizeOnboardingData = (payload = {}) => ({
  adminUser: stripAdminMeta(payload.adminUser || {}),
  company: stripCompanyMeta(payload.company || {}),
  configuration: stripConfigurationMeta(payload.configuration || {}),
  welcome: stripWelcomeMeta(payload.welcome || {})
});

const sanitizeUserProfile = (profile = {}, fallbackUid = null) => ({
  uid: profile.uid || fallbackUid || null,
  firstName: profile.firstName || '',
  lastName: profile.lastName || '',
  email: profile.email || '',
  phone: profile.phone || '',
  role: profile.role || 'admin',
  permissions: profile.permissions || undefined
});

const areSetsEqual = (setA = new Set(), setB = new Set()) => {
  if (setA.size !== setB.size) {
    return false;
  }
  for (const value of setA) {
    if (!setB.has(value)) {
      return false;
    }
  }
  return true;
};

const OnboardingWizard = () => {
  const { currentUser, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = currentUser?.uid || auth.currentUser?.uid || null;
  const stepHandlersRef = useRef({});

  const registerStepHandler = useCallback((stepId, handler) => {
    if (!stepId || typeof handler !== 'function') {
      return () => {};
    }

    stepHandlersRef.current[stepId] = handler;

    return () => {
      if (stepHandlersRef.current[stepId] === handler) {
        delete stepHandlersRef.current[stepId];
      }
    };
  }, []);

  const steps = useMemo(() => ([
    {
      id: 1,
      title: 'Admin Account Setup',
      description: 'Create and verify your administrator account',
      component: AdminUserStep
    },
    {
      id: 2,
      title: 'Company Information',
      description: 'Basic company details and compliance information',
      component: CompanySetupStep
    },
    {
      id: 3,
      title: 'Initial Configuration',
      description: 'Set up default settings and preferences',
      component: InitialConfigStep
    },
    {
      id: 4,
      title: 'Welcome',
      description: 'Complete setup and get started',
      component: WelcomeStep
    }
  ]), []);

  const clampStep = useCallback((step) => {
    if (typeof step !== 'number' || Number.isNaN(step)) {
      return 1;
    }
    return Math.min(Math.max(Math.floor(step), 1), steps.length);
  }, [steps.length]);

  const startStepFromLocation = location.state?.startStep;

  const [currentStep, setCurrentStep] = useState(() => clampStep(startStepFromLocation ?? 1));
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [completeError, setCompleteError] = useState('');
  const [onboardingData, setOnboardingData] = useState({
    company: {},
    adminUser: {},
    configuration: {},
    welcome: {}
  });

  const provideRegisterNextHandler = useCallback((handler) => {
    const stepId = steps[currentStep - 1]?.id;
    if (!stepId || typeof handler !== 'function') {
      return () => {};
    }

    return registerStepHandler(stepId, handler);
  }, [registerStepHandler, currentStep]);

  // Load existing onboarding progress on mount
  useEffect(() => {
    const loadOnboardingProgress = async () => {
      if (!userId) return;

      try {
        const status = await onboardingService.getCompanyOnboardingStatus(userId);
        
        console.log("Onboarding Step", status)
        if (status.hasStarted) {
          const normalizedData = normalizeOnboardingData(status.data);

          try {
            const userProfile = await onboardingService.getUserProfile(userId);
            if (userProfile) {
              const sanitizedProfile = sanitizeUserProfile(userProfile, userId);
              normalizedData.adminUser = {
                ...sanitizedProfile,
                ...normalizedData.adminUser
              };
            }
          } catch (profileError) {
            console.error('Error loading user profile for onboarding:', profileError);
          }

          setCurrentStep(clampStep(status.currentStep));
          setCompletedSteps(new Set(status.completedSteps));
          setOnboardingData(normalizedData);
        } else {
          // Initialize progress for new user
          await onboardingService.initializeCompanyOnboardingProgress(userId);
        }
      } catch (error) {
        console.error('Error loading onboarding progress:', error);
      }
    };

    loadOnboardingProgress();
  }, [userId, clampStep]);

  useEffect(() => {
    if (typeof startStepFromLocation === 'number') {
      const targetStep = clampStep(startStepFromLocation);
      setCurrentStep(prev => (prev === targetStep ? prev : targetStep));
    }
  }, [startStepFromLocation, clampStep]);
  const refreshOnboardingDataFromFirestore = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const [progress, userProfile] = await Promise.all([
        onboardingService.getCompanyOnboardingProgress(userId),
        onboardingService.getUserProfile(userId)
      ]);

      if (!progress) {
        return;
      }

      const normalizedData = normalizeOnboardingData(progress.data);

      if (typeof progress.currentStep === 'number') {
        const remoteStep = clampStep(progress.currentStep);
        setCurrentStep(prev => (prev === remoteStep ? prev : remoteStep));
      }

      if (userProfile) {
        const sanitizedProfile = sanitizeUserProfile(userProfile, userId);
        normalizedData.adminUser = {
          ...sanitizedProfile,
          ...normalizedData.adminUser
        };
      }
      setOnboardingData(prev => (deepEqual(prev, normalizedData) ? prev : normalizedData));

      const remoteCompleted = new Set(progress.completedSteps || []);
      setCompletedSteps(prev => (areSetsEqual(prev, remoteCompleted) ? prev : remoteCompleted));
    } catch (error) {
      console.error('Error refreshing onboarding progress:', error);
    }
  }, [userId, clampStep]);

  const getStepDataForLogging = useCallback((stepId) => {
    const dataKey = STEP_DATA_KEYS[stepId];
    if (!dataKey) {
      return null;
    }

    const stepData = onboardingData[dataKey] || {};

    if (dataKey === 'adminUser') {
      const { password, confirmPassword, ...sanitized } = stepData;
      return sanitized;
    }

    return stepData;
  }, [onboardingData]);

  // Update onboarding data
  const updateOnboardingData = useCallback(async (stepKey, data, options = {}) => {
    if (!stepKey || data === undefined) {
      return;
    }

    const payload = data === null ? {} : data;
    let shouldUpdateRemotely = true;

    setOnboardingData(prev => {
      const previousStepData = prev?.[stepKey] || {};
      const nextStepData = {
        ...previousStepData,
        ...payload
      };

      if (deepEqual(previousStepData, nextStepData)) {
        shouldUpdateRemotely = false;
        return prev;
      }

      return {
        ...prev,
        [stepKey]: nextStepData
      };
    });

    if (!userId) {
      console.log('ℹ️ ONBOARDING: Skipping Firestore save (no authenticated user)', { stepKey });
      return;
    }

    if (options.skipRemote || !shouldUpdateRemotely) {
      console.log('📝 ONBOARDING: Step data updated locally without remote save', {
        stepKey,
        reason: options.skipRemote ? 'skipRemote flag' : 'no data changes'
      });
      return;
    }

    try {
      console.log('💾 ONBOARDING: Saving step data to Firestore:', {
        stepKey,
        payloadKeys: Object.keys(payload || {})
      });

      await onboardingService.initializeCompanyOnboardingProgress(userId);
      await onboardingService.updateCompanyOnboardingData(userId, stepKey, payload);

      console.log('✅ ONBOARDING: Step data saved successfully');
    } catch (error) {
      console.error('❌ ONBOARDING: Error saving onboarding data:', error);
    }
  }, [userId]);

  // Mark step as completed
  const completeStep = async (stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));

    // Save completion to Firestore
    if (userId) {
      try {
        console.log('✅ ONBOARDING: Marking step as completed:', stepId);
        
        // Ensure progress document exists before updating
        await onboardingService.initializeCompanyOnboardingProgress(userId);
        
        // Mark step as completed
        await onboardingService.completeCompanyOnboardingStep(userId, stepId);

        console.log('✅ ONBOARDING: Step completion saved to Firestore');
      } catch (error) {
        console.error('❌ ONBOARDING: Error saving step completion:', error);
      }
    }
  };

  // Check if step is completed
  const isStepCompleted = (stepId) => completedSteps.has(stepId);

  // Handle next step
  const handleNext = async () => {
    const activeStep = steps[currentStep - 1];
    const sanitizedStepData = getStepDataForLogging(currentStep);

    console.log('➡️ ONBOARDING: Next button clicked', {
      stepId: activeStep?.id,
      stepTitle: activeStep?.title,
      stepDescription: activeStep?.description,
      stepData: sanitizedStepData
    });

    const stepHandler = stepHandlersRef.current[activeStep?.id];
    if (stepHandler) {
      let handlerFailed = false;
      let handlerResult;

      setIsLoading(true);
      try {
        handlerResult = await stepHandler();
        console.log('✅ ONBOARDING: Step handler resolved', {
          stepId: activeStep?.id,
          result: handlerResult
        });
      } catch (error) {
        handlerFailed = true;
        console.error('❌ ONBOARDING: Step handler execution failed:', {
          stepId: activeStep?.id,
          error
        });
      } finally {
        setIsLoading(false);
      }

      if (handlerFailed || handlerResult === false) {
        console.warn('⚠️ ONBOARDING: Step handler blocked progression', {
          stepId: activeStep?.id
        });
        return;
      }
    }

    if (currentStep < steps.length) {
      if (userId) {
        console.log('📡 ONBOARDING: Preparing to mark step as complete in Firestore', {
          userId,
          stepId: currentStep
        });
      }

      await completeStep(currentStep);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);

      if (userId) {
        try {
          console.log('💾 ONBOARDING: Saving current step to Firestore', {
            userId,
            nextStep,
            persistedDataPreview: getStepDataForLogging(nextStep - 1)
          });

          await onboardingService.initializeCompanyOnboardingProgress(userId);
          await onboardingService.updateCompanyOnboardingCurrentStep(userId, nextStep);

          console.log('✅ ONBOARDING: Current step saved successfully');
        } catch (error) {
          console.error('❌ ONBOARDING: Error saving current step:', error);
        }
      }
    } else {
      console.log('🧾 ONBOARDING: All steps complete, starting finalization', {
        userId,
        adminData: getStepDataForLogging(1),
        companyData: getStepDataForLogging(2),
        configurationData: getStepDataForLogging(3)
      });
      await handleComplete();
    }
  };

  // Handle previous step
  const handlePrevious = async () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      
      // Save current step to Firestore
      if (userId) {
        try {
          console.log('💾 ONBOARDING: Saving previous step to Firestore:', prevStep);
          
          // Ensure progress document exists before updating
          await onboardingService.initializeCompanyOnboardingProgress(userId);
          
          await onboardingService.updateCompanyOnboardingCurrentStep(userId, prevStep);
          
          console.log('✅ ONBOARDING: Previous step saved successfully');
        } catch (error) {
          console.error('❌ ONBOARDING: Error saving current step:', error);
        }
      }

      await refreshOnboardingDataFromFirestore();
    }
  };

  // Handle step click (navigate to specific step)
  const handleStepClick = async (stepId) => {
    // Allow navigation to completed steps or the next step
    if (isStepCompleted(stepId) || stepId === Math.max(...completedSteps) + 1 || stepId === 1) {
      setCurrentStep(stepId);
      
      // Save current step to Firestore
      if (userId) {
        try {
          console.log('💾 ONBOARDING: Saving clicked step to Firestore:', stepId);
          
          // Ensure progress document exists before updating
          await onboardingService.initializeCompanyOnboardingProgress(userId);
          
          await onboardingService.updateCompanyOnboardingCurrentStep(userId, stepId);
          
          console.log('✅ ONBOARDING: Clicked step saved successfully');
        } catch (error) {
          console.error('❌ ONBOARDING: Error saving current step:', error);
        }
      }

      await refreshOnboardingDataFromFirestore();
    }
  };

  // Complete onboarding process
  const handleComplete = async () => {
    setIsLoading(true);
    setCompleteError('');
    try {
      if (!userId) {
        console.error('❌ ONBOARDING: No authenticated user found');
        throw new Error('User not authenticated');
      }

      console.log('🚀 ONBOARDING: Finalizing onboarding for user:', {
        userId,
        adminData: getStepDataForLogging(1),
        companyData: getStepDataForLogging(2),
        configurationData: getStepDataForLogging(3),
        welcomeData: getStepDataForLogging(4)
      });

      await completeStep(currentStep);

      const progress = await onboardingService.getCompanyOnboardingProgress(userId) || {};

      const mergedCompleted = new Set(
        Array.isArray(progress?.completedSteps) ? progress.completedSteps : []
      );

      if (Array.isArray(progress?.onboardingProgress?.completedSteps)) {
        progress.onboardingProgress.completedSteps
          .filter(step => typeof step === 'number')
          .forEach(step => mergedCompleted.add(step));
      }

      mergedCompleted.add(currentStep);

      const completedStepsArray = Array.from(mergedCompleted);

      // Creates the company if needed and links this user to it, server-side.
      const { companyId } = await onboardingService.finalizeOnboarding(userId, {
        welcomeData: onboardingData?.welcome || {},
        totalSteps: steps.length,
        completedSteps: completedStepsArray
      });

      await refreshUserProfile();

      navigate('/dashboard', {
        replace: true,
        state: {
          onboardingComplete: true,
          companyId
        }
      });
      console.log('✅ ONBOARDING: Navigation to dashboard completed');
    } catch (error) {
      console.error('❌ ONBOARDING: Error completing onboarding:', error);
      setCompleteError(error?.message || 'We could not finish setting up your company. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentStepConfig = steps[currentStep - 1];
  const CurrentStepComponent = currentStepConfig?.component;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <button
                  onClick={() => navigate('/')}
                  className="hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                  title="Go to landing page"
                >
                  <img
                    src="/Himil-logo-header.png"
                    alt="Himil Logistics logo"
                    className="h-10 w-10 object-contain"
                  />
                </button>
              </div>
              <div className="ml-4">
                <h1 className="text-xl font-semibold text-gray-900">
                  Company Onboarding
                </h1>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Onboarding Progress */}
        <OnboardingProgress
          steps={steps.map(step => ({
            id: step.id,
            label: step.title
          }))}
          currentStep={currentStep - 1}
          completedSteps={Array.from(completedSteps)}
          onStepClick={(stepIndex, stepId) => handleStepClick(stepId)}
          showLabels={true}
          orientation="horizontal"
          size="small"
          animated={true}
          className="mb-4"
        />

        {/* Step Content */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              {steps[currentStep - 1]?.title}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {steps[currentStep - 1]?.description}
            </p>
          </div>

          <div className="p-6">
            {completeError && (
              <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {completeError}
              </div>
            )}
            {CurrentStepComponent && (
              <CurrentStepComponent
                data={onboardingData}
                updateData={updateOnboardingData}
                onNext={handleNext}
                onPrevious={handlePrevious}
                canGoNext={true}
                canGoPrevious={currentStep > 1}
                isLoading={isLoading}
                registerNextHandler={provideRegisterNextHandler}
              />
            )}
          </div>

          {/* Navigation Footer */}
          {currentStep < 4 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </button>

              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <>
                    {currentStep === steps.length ? 'Complete Setup' : 'Next'}
                    {currentStep !== steps.length && <ArrowRight className="h-4 w-4 ml-2" />}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
